"use client";

import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useEffect } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

import { useReveal } from "../../../_lib/use-reveal";

// =============================================================================

/**
 * Number that counts up from 0 once in view. Assistive technologies read the
 * final value only.
 */
export function CountUp({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const { ref, isRevealed } = useReveal<HTMLSpanElement>();
  const shouldReduceMotion = usePrefersReducedMotion();
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));

  useEffect(() => {
    if (!isRevealed) return;
    if (shouldReduceMotion) {
      count.set(value);
      return;
    }
    const controls = animate(count, value, {
      duration: 1.8,
      delay: 0.3,
      ease: EASE_OUT_EXPO,
    });
    return () => controls.stop();
  }, [count, isRevealed, shouldReduceMotion, value]);

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{value}</span>
      <motion.span aria-hidden className="tabular-nums">
        {rounded}
      </motion.span>
    </span>
  );
}

// =============================================================================
